import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Card, Input, List, Select, Button, Popconfirm, Tooltip, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import type { ItemWithId } from '../../types/types';
import { useStableCallback, useRenderLoopDetection } from '../../hooks/hookUtils';
import {
  DndContext as DndContextType,
  type DragEndEvent,
  closestCenter,
  type SensorDescriptor,
  type SensorOptions,
} from '@dnd-kit/core';
import { SortableContext, type SortableContextProps } from '@dnd-kit/sortable';
import { SortableItemWrapper } from '../DataPanels/SortableItemWrapper';

const { Search } = Input;

export interface FilterControl<T> {
  key: string;
  placeholder: string;
  options: { label: React.ReactNode; value: unknown }[];
  filterFn: (item: T, value: unknown) => boolean;
}

export interface SortOption<T> {
  label: string;
  value: string | ((a: T, b: T) => number);
}

export interface ConfigurableDataPanelConfig<T extends ItemWithId> {
  title: React.ReactNode;
  items: T[];
  searchPlaceholder: string;
  itemKey: keyof T;
  displayNameKey?: keyof T;
  searchKeys: (keyof T | ((item: T) => string))[];
  filterControls?: FilterControl<T>[];
  sortOptions?: SortOption<T>[];
  initialFilters?: Record<string, unknown>;
  initialSort?: string;
  onStateChange?: (newState: { filters?: Record<string, unknown>; sort?: string }) => void;
  onAddItem?: () => void;
  onEditItem?: (item: T) => void;
  onDeleteItem?: (item: T) => void;
  onDuplicateItem?: (item: T) => void;
  renderActions?: (item: T, defaultActions: React.ReactNode[]) => React.ReactNode[];
  renderItem: (item: T) => React.ReactNode;
  renderList?: (items: T[]) => React.ReactNode; // New optional prop
  dndContext?: {
    DndContext: typeof DndContextType;
    sensors: SensorDescriptor<SensorOptions>[];
    onDragEnd: (event: DragEndEvent) => void;
  };
  sortableContext?: Omit<SortableContextProps, 'children'>;
}

interface ConfigurableDataPanelProps<T extends ItemWithId> {
  config: ConfigurableDataPanelConfig<T>;
}

export const ConfigurableDataPanel = <T extends ItemWithId>({ config }: ConfigurableDataPanelProps<T>) => {
  useRenderLoopDetection('ConfigurableDataPanel');

  const {
    initialFilters, initialSort, onStateChange, ...restConfig
  } = config;

  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, unknown>>(initialFilters ?? {});
  // Use the sort option label as a stable string key (never store function comparators in state).
  // Labels are always strings, so the Select never receives function values as its value prop,
  // which would cause Ant Design's internal state reconciliation to loop when new function
  // references arrive on every parent render.
  const [activeSortKey, setActiveSortKey] = useState<string | undefined>(
    () => initialSort ?? config.sortOptions?.[0]?.label
  );

  /**
   * Create a stable reference to onStateChange to prevent effect re-runs
   * when the callback reference changes but its behavior remains the same.
   * This is critical for preventing render loops when parent components
   * pass inline callbacks.
   */
  const stableOnStateChange = useStableCallback((state: { filters?: Record<string, unknown>; sort?: string }) => {
    onStateChange?.(state);
  });

  /**
   * Track previous filter/sort values to detect real changes.
   * null means "not yet initialized" -- skip the first effect invocation.
   * This pattern is Strict Mode safe: the second invocation sees that
   * prevFiltersRef.current === activeFilters (same reference) and skips,
   * preventing spurious onStateChange calls on mount.
   */
  const prevFiltersRef = useRef<Record<string, unknown> | null>(null);
  const prevSortKeyRef = useRef<string | undefined | null>(null);

  useEffect(() => {
    // null sentinel: first invocation -- record initial values and skip notification
    if (prevFiltersRef.current === null) {
      prevFiltersRef.current = activeFilters;
      prevSortKeyRef.current = activeSortKey;
      return;
    }
    // Only notify parent when filters or sort key actually changed
    if (prevFiltersRef.current !== activeFilters || prevSortKeyRef.current !== activeSortKey) {
      prevFiltersRef.current = activeFilters;
      prevSortKeyRef.current = activeSortKey;
      stableOnStateChange({ filters: activeFilters, sort: activeSortKey });
    }
  }, [activeFilters, activeSortKey, stableOnStateChange]);

  const filteredItems = useMemo(() => {
    // Guard against null/undefined items that could crash sort comparators
    let items = (restConfig.items ?? []).filter((item): item is T => item != null);

    // Apply search term
    if (searchTerm) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      items = items.filter(item =>
        config.searchKeys.some(keyOrAccessor => {
          const value = typeof keyOrAccessor === 'function'
            ? keyOrAccessor(item)
            : item[keyOrAccessor];
          
          return String(value).toLowerCase().includes(lowerCaseSearch);
        })
      );
    }

    // Apply filters
    Object.entries(activeFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        const filterControl = restConfig.filterControls?.find(fc => fc.key === key);
        if (filterControl) {
          items = items.filter(item => filterControl.filterFn(item, value));
        }
      }
    });

    // Find the active sort option by label key (labels are stable strings even when
    // the parent re-renders and creates new function references for sort comparators).
    const activeSortOption = restConfig.sortOptions?.find(opt => opt.label === activeSortKey);

    // Apply sorting: if the found option's value is a function, use it as a comparator.
    // Wrap with null guards so null/undefined items always sort to the end.
    if (activeSortOption && typeof activeSortOption.value === 'function') {
      const sortFn = activeSortOption.value;
      (items ?? []).sort((a, b) => {
        if (a == null) return 1;
        if (b == null) return -1;
        return sortFn(a, b);
      });
    }

    return items;
  }, [restConfig.items, searchTerm, activeFilters, activeSortKey, config.searchKeys, restConfig.filterControls, restConfig.sortOptions]);

  /**
   * Handle filter changes from the filter dropdowns.
   * Uses useStableCallback to maintain a stable reference for Select onChange props.
   */
  const handleFilterChange = useStableCallback((key: string, value: unknown) => {
    setActiveFilters(prev => ({ ...prev, [key]: value }));
  });

  /**
   * Handle sort changes from the sort dropdown.
   * Receives the sort option label (a string key), never a function.
   * Uses useStableCallback to maintain a stable reference for Select onChange prop.
   */
  const handleSortChange = useStableCallback((key: string | undefined) => {
    setActiveSortKey(key);
  });

  const { onDuplicateItem, onEditItem, onDeleteItem, displayNameKey } = config;

  const defaultActions = useCallback((item: T) => [
      onDuplicateItem && (
        <Tooltip title="Duplicate" key="duplicate">
          <Button icon={<CopyOutlined />} onClick={() => onDuplicateItem?.(item)} type="text" />
        </Tooltip>
      ),
      onEditItem && (
        <Tooltip title="Edit" key="edit">
          <Button icon={<EditOutlined />} onClick={() => onEditItem?.(item)} type="text" />
        </Tooltip>
      ),
      onDeleteItem && (
        <Popconfirm
          key="delete"
          title={`Delete "${String(item[displayNameKey ?? 'name'] || 'this item')}"?`}
          onConfirm={() => onDeleteItem?.(item)}
        >
          <Tooltip title="Delete">
            <Button icon={<DeleteOutlined />} type="text" danger />
          </Tooltip>
        </Popconfirm>
      ),
    ].filter(Boolean),
    [onDuplicateItem, onEditItem, onDeleteItem, displayNameKey]
  );

  const listContent = useMemo(() => {
    if (restConfig.renderList) {
      return restConfig.renderList(filteredItems);
    }

    const listItems = (filteredItems ?? []).map(item => {
      const listItem = (
        <List.Item actions={restConfig.renderActions ? restConfig.renderActions(item, defaultActions(item) as React.ReactNode[]) : defaultActions(item)}>
          {config.renderItem(item)}
        </List.Item>
      );

      // If dndContext is provided, wrap the item in a sortable wrapper
      if (restConfig.dndContext && restConfig.sortableContext) {
        return (
          <SortableItemWrapper key={item.id} item={item}>
            {({ attributes, listeners }) => (
              <div {...attributes} {...listeners}>
                {listItem}
              </div>
            )}
          </SortableItemWrapper>
        );
      }
      return listItem;
    });

    return <List itemLayout="horizontal">{listItems}</List>;
  }, [filteredItems, restConfig, config, defaultActions]);

  const dndEnabled = !!(restConfig.dndContext && restConfig.sortableContext);

  // Destructure DndContext for use in JSX if it exists.
  const DndContextComponent = restConfig.dndContext?.DndContext;

  return (
    <Card
      title={config.title}
      extra={restConfig.onAddItem && <Button icon={<PlusOutlined />} onClick={restConfig.onAddItem}>Add</Button>}
      style={{ margin: '16px' }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space wrap>
          <Search
            placeholder={config.searchPlaceholder}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ minWidth: 200, flexGrow: 1 }}
            allowClear
          />
          {restConfig.filterControls?.map(fc => (
            <Select
              key={fc.key}
              placeholder={fc.placeholder}
              options={fc.options}
              onChange={value => handleFilterChange(fc.key, value)}
              style={{ minWidth: 150 }}
              allowClear
            />
          ))}
          {restConfig.sortOptions && (
            <Select
              placeholder="Sort by..."
              value={activeSortKey}
              onChange={handleSortChange}
              style={{ minWidth: 150 }}
              allowClear
              options={restConfig.sortOptions.map(opt => ({ label: opt.label, value: opt.label }))}
            />
          )}
        </Space>

        {dndEnabled && DndContextComponent ? (
          <DndContextComponent
            sensors={restConfig.dndContext!.sensors}
            collisionDetection={closestCenter}
            onDragEnd={restConfig.dndContext!.onDragEnd}
          >
            <SortableContext items={restConfig.sortableContext!.items} strategy={restConfig.sortableContext!.strategy}>
              {listContent}
            </SortableContext>
          </DndContextComponent>
        ) : (
          listContent
        )}
      </Space>
    </Card>
  );
};